import { useEffect, useMemo, useState } from 'react';

import { OrderExecutionTermsOrderType } from 'api/types/entity/order/orderExecutionTermsEntity';
import { BigNumber } from 'bignumber.js';
import { DEFAULT_NOMINAL_VALUE } from 'modules/order/common/constants';
import { useField } from 'modules/order/common/forms/orderForm';

import { useCreateOrderContext } from '../../../../createOrder/providers/OrderProvider';

export const useTransactionVolumeSection = () => {
  const { directories } = useCreateOrderContext();

  const { financialInstruments, currencies } = directories;

  const [nominalQuantity, setNominalQuantity] = useState<string>();

  const { field: nominalValueField } = useField(
    'financialInstrument.nominalValue'
  );

  const { field: quantityField } = useField('transactionVolume.quantity');
  const { field: unitPriceField, helpers: unitPriceHelpers } = useField(
    'transactionVolume.unitPrice'
  );
  const { field: orderTypeField } = useField('executionTerms.orderType');

  const { helpers: transactionAmountHelpers } = useField(
    'transactionVolume.transactionAmount'
  );

  const isDisabledUnitPrice =
    orderTypeField?.value?.value !== OrderExecutionTermsOrderType.Limit;

  const isTransactionAmountDisabled = useMemo(
    () =>
      isDisabledUnitPrice ||
      unitPriceField.value === undefined ||
      quantityField.value === undefined,
    [isDisabledUnitPrice, quantityField.value, unitPriceField.value]
  );

  useEffect(() => {
    if (quantityField.value !== undefined) {
      const nominalValue = BigNumber(
        (nominalValueField?.value as string) || DEFAULT_NOMINAL_VALUE
      );

      setNominalQuantity(
        nominalValue.multipliedBy(quantityField.value).toFixed()
      );
    } else {
      setNominalQuantity(undefined);
    }
  }, [financialInstruments, quantityField.value, nominalValueField?.value]);

  useEffect(() => {
    if (isDisabledUnitPrice) {
      unitPriceHelpers.setValue(undefined);
      unitPriceHelpers.setError(undefined);
      transactionAmountHelpers.setError(undefined);
      transactionAmountHelpers.setValue(undefined);
      return;
    }
    if (
      unitPriceField.value !== undefined &&
      quantityField.value !== undefined
    ) {
      const bigNumber = BigNumber(unitPriceField.value);
      if (bigNumber.isNaN()) {
        transactionAmountHelpers.setValue(undefined);
        return;
      }
      transactionAmountHelpers.setValue(
        bigNumber.multipliedBy(quantityField.value).toFixed()
      );
    } else {
      transactionAmountHelpers.setValue(undefined);
    }
  }, [
    transactionAmountHelpers,
    quantityField.value,
    unitPriceField.value,
    unitPriceHelpers,
    isDisabledUnitPrice,
  ]);

  return {
    isDisabledUnitPrice,
    isTransactionAmountDisabled,
    financialInstruments,
    currencies,
    nominalQuantity,
  };
};
